import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { jobListings, jobCategories, employmentStats, topEmployers, careerResources } from '../../mocks/jobs';
import MailchimpNewsletter from '../../components/feature/MailchimpNewsletter';

export default function JobsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState<number | null>(null);

  useSEO({
    title: 'Jobs in Roseville CA - Employment Opportunities & Career Listings',
    description: 'Find jobs in Roseville California. Browse employment opportunities, career listings, and hiring companies in Roseville CA. Search full-time, part-time, and remote positions.',
    keywords: 'jobs in Roseville CA, Roseville California jobs, employment Roseville, careers Roseville, hiring in Roseville, job openings Roseville, work in Roseville CA',
    canonical: '/jobs',
    ogImage: 'https://public.readdy.ai/ai/img_res/70246dc79f5f348603177f11d893f5f6.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Jobs & Employment',
          description: 'Job listings and employment opportunities in Roseville, California',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/jobs`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/` },
            { '@type': 'ListItem', position: 2, name: 'Jobs & Careers', item: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/jobs` },
          ],
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is the job market like in Roseville CA?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Roseville has a strong and growing job market with a low unemployment rate. Major employers include healthcare systems, technology companies, retail, government, and education sectors. The city is home to several Fortune 500 employers.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the average salary in Roseville California?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The average salary in Roseville, CA is approximately $72,000 per year, varying by industry. Technology and healthcare roles typically offer higher compensation, while retail and hospitality positions range lower.',
              },
            },
          ],
        },
      ],
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredJobs = jobListings.filter(job => {
    const matchesCategory = selectedCategory === 'All' || job.category === selectedCategory;
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredJobs = jobListings.filter(job => job.featured);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Home</Link>
              <Link to="/recreation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Recreation</Link>
              <Link to="/real-estate" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Real Estate</Link>
              <Link to="/utilities" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Utilities</Link>
              <Link to="/jobs" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-teal-600' : 'text-teal-300'
              }`}>Jobs</Link>
            </div>

            <Link to="/" className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
              Get Guide
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20in%20California%20with%20professionals%20walking%2C%20business%20district%2C%20sunny%20day%2C%20corporate%20campus%2C%20tech%20hub%20atmosphere%2C%20diverse%20workforce%2C%20professional%20environment&width=1920&height=1080&seq=jobshero001&orientation=landscape"
            alt="Roseville Jobs and Employment"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Jobs & Careers
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Discover exciting career opportunities in Roseville. From tech giants to healthcare leaders, 
            find your next role in one of California's fastest-growing cities.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-full p-2 flex items-center shadow-xl">
              <div className="flex-1 flex items-center px-4">
                <i className="ri-search-line text-gray-400 text-xl mr-3"></i>
                <input
                  type="text"
                  placeholder="Search jobs, companies, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full py-2 text-gray-900 placeholder-gray-500 focus:outline-none"
                />
              </div>
              <button className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Search Jobs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-teal-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{employmentStats.totalJobs}</div>
              <div className="text-teal-100">Available Jobs</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{employmentStats.avgSalary}</div>
              <div className="text-teal-100">Average Salary</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{employmentStats.unemploymentRate}</div>
              <div className="text-teal-100">Unemployment Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">{employmentStats.topEmployers}</div>
              <div className="text-teal-100">Fortune 500 Employers</div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="text-center mt-8 pt-6 border-t border-teal-500/30">
            <p className="text-teal-100 text-sm">
              Data provided by U.S. Bureau of Labor Statistics, California EDD, and Roseville Area Chamber of Commerce
            </p>
            <p className="text-teal-200/50 text-xs mt-1">
              <i className="ri-time-line mr-1"></i>Data last updated: March 5, 2025
              <span className="mx-2">•</span>
              <a href="/data-sources" className="text-teal-200/70 hover:text-white font-medium transition-colors cursor-pointer">
                View All Sources <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Opportunities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hot jobs from top employers actively hiring in Roseville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100 cursor-pointer group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img src={job.logo} alt={job.company} className="w-full h-full object-cover" />
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                    Featured
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">{job.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{job.company}</p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-map-pin-line mr-2 text-teal-500"></i>
                    <span>{job.location}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="ri-money-dollar-circle-line mr-2 text-teal-500"></i>
                    <span>{job.salary}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{job.posted}</span>
                  <span className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full">
                    {job.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Jobs */}
      <section id="jobs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Browse All Jobs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore career opportunities across all industries in Roseville
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {jobCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Jobs List */}
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className={`bg-white rounded-xl border transition-all ${
                  selectedJob === job.id ? 'border-teal-500 shadow-lg' : 'border-gray-200 hover:border-teal-300 hover:shadow-md'
                }`}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={job.logo} alt={job.company} className="w-full h-full object-cover" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-gray-900">{job.title}</h3>
                        {job.featured && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{job.company}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <i className="ri-map-pin-line mr-1 text-teal-500"></i>
                          {job.location}
                        </span>
                        <span className="flex items-center">
                          <i className="ri-money-dollar-circle-line mr-1 text-teal-500"></i>
                          {job.salary}
                        </span>
                        <span className="flex items-center">
                          <i className="ri-time-line mr-1 text-teal-500"></i>
                          {job.posted}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="px-4 py-1.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full">
                        {job.category}
                      </span>
                      <span className="px-4 py-1.5 bg-teal-50 text-teal-700 text-sm font-semibold rounded-full">
                        {job.type}
                      </span>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${
                        selectedJob === job.id ? 'rotate-180' : ''
                      }`}></i>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedJob === job.id && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-6">
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Job Description</h4>
                        <p className="text-gray-600 mb-6">{job.description}</p>
                        
                        <h4 className="font-semibold text-gray-900 mb-3">Requirements</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, index) => (
                            <li key={index} className="flex items-center text-gray-600">
                              <i className="ri-checkbox-circle-line text-teal-500 mr-2"></i>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Benefits</h4>
                        <div className="flex flex-wrap gap-2 mb-6">
                          {job.benefits.map((benefit, index) => (
                            <span key={index} className="px-3 py-1.5 bg-teal-50 text-teal-700 text-sm rounded-full">
                              {benefit}
                            </span>
                          ))}
                        </div>
                        
                        <div className="bg-gray-50 rounded-xl p-6">
                          <h4 className="font-semibold text-gray-900 mb-4">Ready to Apply?</h4>
                          <button className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer mb-3">
                            Apply Now
                          </button>
                          <button className="w-full bg-white border border-gray-200 hover:border-teal-500 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                            Save Job
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Top Employers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Top Employers in Roseville</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Leading companies that call Roseville home
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topEmployers.map((employer, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer">
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={employer.logo}
                    alt={employer.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-900 text-xs font-semibold rounded-full">
                      {employer.industry}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 mb-1">{employer.name}</h3>
                  <p className="text-sm text-gray-600">
                    <i className="ri-team-line mr-1 text-teal-500"></i>
                    {employer.employees} employees
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Career Resources</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Free resources to help you land your dream job in Roseville
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {careerResources.map((resource, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all cursor-pointer group">
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${resource.icon} text-teal-600 text-2xl group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-sm text-gray-600">{resource.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Your Career in Roseville?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who have found rewarding careers in one of California's 
            best places to live and work. Sign up for job alerts and never miss an opportunity.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#jobs"
              className="bg-white text-teal-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Browse All Jobs
            </a>
            <a
              href="https://www.roseville.ca.us/government/departments/human_resources/employment_opportunities"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-teal-700 hover:bg-teal-800 text-white px-8 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              City Jobs Portal
            </a>
          </div>
        </div>
      </section>

      <MailchimpNewsletter />

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="text-2xl font-bold mb-4 block cursor-pointer">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </Link>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Your guide to living and working in Roseville, California
              </p>
              <p className="text-white/80 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, California 95747
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/recreation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Recreation</Link></li>
                <li><Link to="/real-estate" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Real Estate</Link></li>
                <li><Link to="/utilities" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Utilities</Link></li>
                <li><Link to="/jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Jobs</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Job Categories</h4>
              <ul className="space-y-2">
                <li><a href="#jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Technology</a></li>
                <li><a href="#jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Healthcare</a></li>
                <li><a href="#jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Government</a></li>
                <li><a href="#jobs" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Education</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect</h4>
              <div className="flex space-x-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">
                © 2025 Roseville.org. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Website Builder</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
